import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Container, Card, CardContent } from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Person as PersonIcon,
  Face as FaceIcon,
  LocationOff as LocationOffIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getNavIconForPath } from '../../../utils/navIcons';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import FilterBar from '../../common/FilterBar';
import TableList from '../../common/TableList';
import FeedbackMessage from '../../common/FeedbackMessage';
import api from '../../../services/api';
import { formatMalaysiaDateTime } from '../../../utils/malaysiaTime';
import { glassCardSx } from '../../common/SaaSTokens';

const STAT_FILTER_MAP = ['all', 'success', 'unsuccessful', 'outside_area'];

const RECOGNITION_TYPE_LABELS = {
  public_attendance: 'Public check-in',
  checkout: 'Public check-out',
  attendance: 'Staff check-in',
  registration: 'Face registration',
  removal: 'Face removal',
};

const STATUS_LABELS = {
  success: 'Success',
  failed: 'Failed',
  no_face: 'No face detected',
  multiple_faces: 'Multiple faces',
  unknown_face: 'Unknown face',
};

const RECOGNITION_TYPE_ORDER = [
  'public_attendance',
  'checkout',
  'attendance',
  'registration',
  'removal',
];

const isOutsideAreaLog = (row) =>
  row?.metadata?.reason === 'outside_area' || row?.metadata?.outside_designated_area === true;

const recognitionTypeLabel = (type) => RECOGNITION_TYPE_LABELS[type] || type || '-';

const statusColor = (row) => {
  if (row?.status === 'success') return 'success';
  if (isOutsideAreaLog(row)) return 'warning';
  if (row?.status === 'no_face' || row?.status === 'multiple_faces') return 'warning';
  return 'error';
};

const statusLabel = (row) => {
  if (row?.metadata?.reason === 'outside_area') return 'Outside area';
  if (row?.metadata?.outside_designated_area) {
    const base = STATUS_LABELS[row?.status] || row?.status || 'Failed';
    return `${base} (off-site)`;
  }
  return STATUS_LABELS[row?.status] || row?.status || '-';
};

const MotionCard = motion(Card);

const FaceAttendanceLogs = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [filters, setFilters] = useState({
    recognition_type: '',
    status: '',
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const run = async () => {
      try {
        setError('');
        const res = await api.get('/face/logs', {
          params: { per_page: 500 },
          cache: false,
        });
        setLogs(res.data?.data || []);
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to load face attendance logs.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const filterOptions = useMemo(() => {
    const typesInData = new Set(logs.map((r) => r.recognition_type).filter(Boolean));
    const typeOptions = RECOGNITION_TYPE_ORDER
      .filter((t) => typesInData.has(t))
      .concat([...typesInData].filter((t) => !RECOGNITION_TYPE_ORDER.includes(t)))
      .map((value) => ({ value, label: recognitionTypeLabel(value) }));

    const statusesInData = new Set(logs.map((r) => r.status).filter(Boolean));
    const statusOptions = Object.keys(STATUS_LABELS)
      .filter((value) => statusesInData.has(value))
      .map((value) => ({ value, label: STATUS_LABELS[value] }));

    if (logs.some(isOutsideAreaLog)) {
      statusOptions.push({ value: 'outside_area', label: 'Outside designated area' });
    }

    return [
      {
        name: 'recognition_type',
        label: 'Scan type',
        type: 'select',
        options: typeOptions,
      },
      {
        name: 'status',
        label: 'Result',
        type: 'select',
        options: statusOptions,
      },
    ];
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return logs.filter((r) => {
      if (activeStatFilter === 'success' && r.status !== 'success') return false;
      if (activeStatFilter === 'unsuccessful' && r.status === 'success') return false;
      if (activeStatFilter === 'outside_area' && !isOutsideAreaLog(r)) return false;

      if (filters.recognition_type && r.recognition_type !== filters.recognition_type) return false;
      if (filters.status) {
        if (filters.status === 'outside_area') {
          if (!isOutsideAreaLog(r)) return false;
        } else if (r.status !== filters.status) return false;
      }

      if (!q) return true;
      const who = r?.user ? `${r.user.name} ${r.user.email}` : 'Unknown / public';
      const reason = r?.metadata?.reason || '';
      const action = r?.metadata?.action || '';
      return [
        who,
        r.recognition_type,
        recognitionTypeLabel(r.recognition_type),
        r.status,
        statusLabel(r),
        reason,
        action,
      ].join(' ').toLowerCase().includes(q);
    });
  }, [logs, filters, searchTerm, activeStatFilter]);

  // Stats from all loaded logs (not the filtered subset), same as UserList
  const statsData = useMemo(() => {
    const successCount = logs.filter((r) => r.status === 'success').length;
    const outsideCount = logs.filter(isOutsideAreaLog).length;
    return [
      { icon: FaceIcon, value: logs.length, label: 'Total Scans', color: 'info' },
      { icon: CheckCircleIcon, value: successCount, label: 'Successful', color: 'success' },
      { icon: ErrorOutlineIcon, value: logs.length - successCount, label: 'Unsuccessful', color: 'error' },
      { icon: LocationOffIcon, value: outsideCount, label: 'Outside Area', color: 'warning' },
    ];
  }, [logs]);

  const columns = [
    {
      key: 'recognized_at',
      label: 'Scan Time',
      icon: AccessTimeIcon,
      sortable: true,
      primary: true,
      render: (row) => (row.recognized_at ? formatMalaysiaDateTime(row.recognized_at) : '-'),
    },
    {
      key: 'user',
      label: 'User',
      icon: PersonIcon,
      sortable: false,
      render: (row) => (row.user ? `${row.user.name} (${row.user.email})` : 'Unknown / public'),
    },
    {
      key: 'recognition_type',
      label: 'Scan type',
      icon: LoginIcon,
      sortable: true,
      sortAccessor: (row) => recognitionTypeLabel(row.recognition_type),
      render: (row) => recognitionTypeLabel(row.recognition_type),
    },
    {
      key: 'status',
      label: 'Result',
      sortable: true,
      sortAccessor: (row) => statusLabel(row),
      render: (row) => (
        <Chip
          size="small"
          label={statusLabel(row)}
          color={statusColor(row)}
          variant="outlined"
        />
      ),
    },
    {
      key: 'reason',
      label: 'Reason',
      sortable: true,
      sortAccessor: (row) => row?.metadata?.reason || '',
      render: (row) => row?.metadata?.reason || '-',
    },
    {
      key: 'action',
      label: 'Action',
      sortable: true,
      sortAccessor: (row) => row?.metadata?.action || '',
      render: (row) => row?.metadata?.action || '-',
    },
    {
      key: 'faces',
      label: 'Faces',
      sortable: true,
      sortAccessor: (row) => (Array.isArray(row.detected_faces_count) ? row.detected_faces_count[0] : -1),
      render: (row) => (
        Array.isArray(row.detected_faces_count) && row.detected_faces_count.length
          ? row.detected_faces_count[0]
          : '-'
      ),
    },
    {
      key: 'confidence_score',
      label: 'Confidence',
      sortable: true,
      render: (row) => (
        typeof row.confidence_score === 'number' ? `${(row.confidence_score * 100).toFixed(1)}%` : '-'
      ),
    },
  ];

  const handleClearFilters = () => {
    setSearchTerm('');
    setActiveStatFilter('all');
    setFilters({ recognition_type: '', status: '' });
    setPage(0);
  };

  const handleStatCardClick = (_, index) => {
    const selected = STAT_FILTER_MAP[index] || 'all';
    const next = activeStatFilter === selected ? 'all' : selected;
    setActiveStatFilter(next);

    const nextFilters = { ...filters };
    delete nextFilters.status;
    if (next === 'success') {
      nextFilters.status = 'success';
    } else if (next === 'outside_area') {
      nextFilters.status = 'outside_area';
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex = Math.max(0, STAT_FILTER_MAP.indexOf(activeStatFilter));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <Loading message="Loading face attendance logs" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Attendance Logs"
        subtitle="Face check-in and check-out scan history (success and failure)"
        icon={getNavIconForPath('/attendance/logs')}
      />

      <StatCards
        stats={statsData}
        activeIndex={activeStatIndex}
        onCardClick={handleStatCardClick}
      />

      <FeedbackMessage message={error} severity="error" onClose={() => setError('')} />

      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...glassCardSx, mb: 2.5 }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setPage(0);
            }}
            filters={filters}
            onFilterChange={(next) => {
              setFilters(next);
              setPage(0);
            }}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            searchPlaceholder="Search by user, scan type, result, or reason..."
          />
        </CardContent>
      </MotionCard>

      <TableList
        columns={columns}
        data={filteredLogs}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        loading={false}
        streamLabel="FACE ATTENDANCE LOGS"
        emptyState={{
          icon: <FaceIcon />,
          title: 'No attendance logs found',
          description: 'No records match your current filters.',
        }}
      />
    </Container>
  );
};

export default FaceAttendanceLogs;
